import {
  debounce,
  getRiskFactorData,
  getRiskFactorMaximumScore,
  getTransitionDuration,
  setupLogger,
} from '../utils.js'

const { logger, loggerError } = setupLogger('ScoreMeter', 'yellow')


export default class ScoreMeter extends HTMLElement {
  static observedAttributes = ['value']

  constructor() {
    super()
    logger(this)

    this.initial = true
    this.controllerId = this.getAttribute('controller')
    this.labelText = this.getAttribute('label')
    this.maxValue = getRiskFactorMaximumScore()
    const currentValue = this.getAttribute('value')

    this.container = this.containerTemplate(currentValue)
    this.append(this.container)

    this.renderScore(currentValue, null)
  }

  connectedCallback() {
    // Debounce the listener's callback a bit to batch updates:
    document.addEventListener('reef:signal-risk-selection', debounce((event) => {
      // logger('heard reef:signal-risk-selection', event.detail)
      const selectedRiskFactors = getRiskFactorData(Array.from(event.detail))
      // logger('selected risk factors', selectedRiskFactors)
      const score = selectedRiskFactors.reduce((sum, currentItem) => currentItem.score ? sum + currentItem.score : sum, 0)
      this.setAttribute('value', score)
    }, 50))

    this.controller = document.querySelector(`#${this.controllerId}`)
    // Detect first user interaction with the assessment form:
    this.controller.addEventListener('change', (event) => {
      this.initial = false
    }, { once: true })
  }

  attributeChangedCallback(attr, oldValue, newValue) {
    // Assuming the current value will be the only attribute changing:
    if (attr !== 'value') return

    // Ignore initial callback if there's nothing to update:
    if (oldValue === null && newValue === '0') return

    this.renderScore(newValue, oldValue)
  }

  renderScore(newValue, oldValue) {
    this.output.innerHTML = this.meterTemplate(this.maxValue, newValue)
    this.output.dataset.value = newValue
    this.output.style.setProperty('--value', newValue)

    if (oldValue && !this.initial) {
      const diff = parseInt(newValue) - parseInt(oldValue)
      const diffToast = this.diffTemplate(diff)
      this.container.append(diffToast)
      const animationDuration = getTransitionDuration(diffToast)
      setTimeout(() => {
        diffToast.remove()
      }, animationDuration)
    }
  }

  containerTemplate(currentValue) {
    const container = document.createElement('div')
    container.classList.add('score-meter-container')

    const label = document.createElement('label')
    label['for'] = `${this.controllerId}-score`
    label.innerText = this.labelText
    container.append(label)

    this.output = document.createElement('output')
    this.output.classList.add('score-meter-display')
    this.output['for'] = this.controllerId
    this.output.id = `${this.controllerId}-score`
    this.output.role = 'status'
    this.output.dataset.value = currentValue
    this.output.dataset.maxValue = this.maxValue
    this.output.style.setProperty('--max-value', this.maxValue)
    container.append(this.output)

    return container
  }

  meterTemplate(maxValue, currentValue) {
    return `<meter min="0" max="${maxValue}" value="${currentValue}">${currentValue}</meter>`
  }

  diffTemplate(diffValue) {
    const isPositive = Math.sign(diffValue) === 1
    const diffToast = document.createElement('span')
    diffToast.classList.add('score-meter-diff')
    diffToast.dataset.isPositive = isPositive
    diffToast.innerText = isPositive ? `+${diffValue}` : diffValue
    return diffToast
  }
}
